
/**
 * Module dependencies.
 */

import { TypologyName } from 'src/types/products';
import { color, units } from 'src/styles/utils';
import {
  typologiesIconsLarge,
  typologiesValues
} from 'src/core/collections-config/typologies';

import { useTranslate } from 'src/core/utils/translator';
import CardTitle from 'src/components/core/typography/card-title';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  name: TypologyName
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  text-transform: uppercase;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg).attrs({ size: units(12) })`
  margin-bottom: ${units(2.5)};
`;

/**
 * `Label` styled component.
 */

const Label = styled(Type.Small).attrs({ as: 'span' })`
  color: ${color('grey900')};
  margin-bottom: ${units(1)};
`;

/**
 * `Title` styled component.
 */

const Title = styled(CardTitle).attrs({ as: 'span', size: 'small' })`
  letter-spacing: 0.5px;
  line-height: 32px;
`;

/**
 * `Typology` component.
 */

const Typology = ({ name, ...rest }: Props): ReactElement | null => {
  const { translate } = useTranslate();

  if (!Object.keys(typologiesValues).includes(name)) {
    return null;
  }

  return (
    <Wrapper {...rest}>
      <Icon icon={typologiesIconsLarge[name]} />

      <Label>
        {translate('common:labels.typology')}
      </Label>

      <RawHtml element={Title}>
        {translate(`collections:typologies.${name}`).split(' ').join('<br />')}
      </RawHtml>
    </Wrapper>
  );
};

/**
 * Export `Typology` component.
 */

export default Typology;
